import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
    Translation,
    TranslocoLoader,
    TranslocoService,
} from '@jsverse/transloco';
import { lastValueFrom, retry, throwError, timer } from 'rxjs';
import { UserService } from '../services/user.service';
import { CognitoAuthService } from '../auth/cognito-auth.service';
import { HttpCacheManager, withCache } from '@ngneat/cashew';
import { TranslocoCacheTime } from './transloco-cache.const';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    private recoveringLangs = new Set<string>();

    constructor(
        private httpClient: HttpClient,
        private userService: UserService,
        private cacheManager: HttpCacheManager,
        private cognitoAuthService: CognitoAuthService,
        private injector: Injector
    ) {}

    async getTranslation(lang: string): Promise<Translation> {
        let language: string;
        let module: string;
        if (lang.includes('/')) {
            const parts = lang.split('/');
            module = parts[0] ?? 'page';
            language = parts[1] ?? lang;
        } else {
            language = lang;
            module = 'page';
        }

        if (module === 'codext') {
            return await lastValueFrom(
                this.httpClient.get<Translation>(
                    `/assets/i18n/${language}.json`
                )
            );
        }

        // System_translations.php answers 401 for module requests unless the
        // PHP session is logged in. Right after a Cognito login the token
        // exchange may still be in flight, so wait for it before fetching —
        // otherwise the empty result sticks in transloco's in-memory store
        // and users see literal keys until they reload the page.
        await this.waitForSession();

        const cacheKey = this.buildCacheKey(module, language);
        const cached = await this.fetchModuleTranslations(
            module,
            language,
            cacheKey,
            true
        );
        if (this.isValidTranslation(cached)) {
            return cached;
        }

        // The cache delivered (or just stored) an empty/invalid payload — drop it
        // and try again live so users don't keep seeing literal translation keys
        // until the 1-day TTL expires.
        this.cacheManager.delete(cacheKey);
        const fresh = await this.fetchModuleTranslations(
            module,
            language,
            cacheKey,
            false
        );
        if (this.isValidTranslation(fresh)) {
            return fresh;
        }

        // Still nothing. Transloco will keep this empty result in memory for
        // the rest of the session, so retry in the background and push the
        // translations in once the backend answers.
        this.scheduleRecovery(lang, module, language, cacheKey);
        return {} as Translation;
    }

    private async waitForSession(): Promise<void> {
        // Safety net: never stall translation loading indefinitely.
        const timeout = new Promise<void>(resolve =>
            setTimeout(resolve, 10000)
        );
        await Promise.race([this.cognitoAuthService.ensureSession(), timeout]);
    }

    /**
     * Background self-healing for a failed module load: retry with backoff
     * and merge the translations into transloco's store so already-rendered
     * views recover without a full page reload.
     */
    private scheduleRecovery(
        lang: string,
        module: string,
        language: string,
        cacheKey: string
    ): void {
        if (this.recoveringLangs.has(lang)) {
            return;
        }
        this.recoveringLangs.add(lang);
        const sleep = (ms: number): Promise<void> =>
            new Promise(resolve => setTimeout(resolve, ms));
        const run = async (): Promise<void> => {
            // While anonymous the backend keeps answering 401 for module
            // requests, so don't burn the retries yet — wait until the user
            // signs in (e.g. they are still on the sign-in page).
            let waited = 0;
            const maxWait = 10 * 60 * 1000;
            while (!this.userService.getUser()?.uid && waited < maxWait) {
                await sleep(3000);
                waited += 3000;
            }
            for (const delay of [2000, 5000, 15000, 30000]) {
                await sleep(delay);
                const result = await this.fetchModuleTranslations(
                    module,
                    language,
                    cacheKey,
                    false
                );
                if (this.isValidTranslation(result)) {
                    // Lazy lookup — injecting TranslocoService in the
                    // constructor would be a circular dependency.
                    this.injector
                        .get(TranslocoService)
                        .setTranslation(result, lang);
                    return;
                }
            }
        };
        run().finally(() => this.recoveringLangs.delete(lang));
    }

    private buildCacheKey(module: string, language: string): string {
        // Keying by uid prevents an empty/anonymous response from polluting
        // the cache for a logged-in user (and vice versa).
        const uid = this.userService?.user?.uid ?? 'anon';
        return `translate-${module}-${language}-${uid}`;
    }

    private async fetchModuleTranslations(
        module: string,
        language: string,
        cacheKey: string,
        useCache: boolean
    ): Promise<Translation | null> {
        const uid = this.userService?.user?.uid;
        const baseUrl = `hippo/navigator.php?page=pages/system/System_translations.php&page_type=json${
            uid ? `&uid=${uid}` : ''
        }`;
        const formData = new FormData();
        formData.append('iso_code', language);
        formData.append('module', module);

        try {
            const options = useCache
                ? {
                      context: withCache({
                          ttl: TranslocoCacheTime,
                          key: cacheKey,
                      }),
                  }
                : {};
            const response = await lastValueFrom(
                this.httpClient
                    .post<{ result?: Translation }>(baseUrl, formData, options)
                    .pipe(
                        retry({
                            count: 2,
                            delay: (err: unknown, retryCount: number) => {
                                // Don't retry auth/permission errors — only transient ones.
                                if (err instanceof HttpErrorResponse) {
                                    const status = err.status;
                                    if (status >= 400 && status < 500) {
                                        return throwError(() => err);
                                    }
                                }
                                return timer(500 * retryCount);
                            },
                        })
                    )
            );
            const result = response?.result ?? null;
            if (useCache && !this.isValidTranslation(result)) {
                this.cacheManager.delete(cacheKey);
            }
            return result;
        } catch (error) {
            if (useCache) {
                this.cacheManager.delete(cacheKey);
            }
            return null;
        }
    }

    private isValidTranslation(value: unknown): value is Translation {
        return (
            !!value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value as object).length > 0
        );
    }
}
